import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'pie',
  name_en: 'Pie',
  // name_zh: '饼图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    data: amount => amount > 0 && amount <= 10,
    x: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    }
  }
};

/**
 * ECharts 的配置项
 * @see https://echarts.apache.org/zh/option.html#series-line
 */
type EChartsPieSeries = {
  roseType?: unknown;
  radius?: unknown;
};

export interface Spec extends StandardChartLayerSpec, EChartsPieSeries {
  encoding: {
    x: string;
    y: string;
  };
}

export const layer = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: false,
  visInfo,
  render(api, spec) {
    const { data: dataArray, util, hook } = api;
    const {
      dataIndex = 0,
      encoding: { x, y: tempY },
      roseType = false,
      radius
    } = spec;
    const { proxy } = api.spec;
    // 兜底数组情况，饼图y字段默认不支持数组
    const y = (Array.isArray(tempY) ? tempY[0] : tempY) as string;
    const data = dataArray[dataIndex];
    const dataMetaInfo = hook.parseDataMetaInfo(data);

    // * -------------------------- data
    const transformData = util.dataTransform.transform(data.values.slice(), [
      { type: 'deDuplication', field: x }
    ]) as typeof data.values;
    const seriesData = transformData.map(item => ({
      name: item[x],
      value: +item[y]
    }));

    // * -------------------------- chart
    let seriesOption = {} as unknown;
    if (typeof roseType !== 'undefined') {
      seriesOption = {
        roseType
      };
    }
    if (typeof radius !== 'undefined') {
      seriesOption = {
        radius
      };
    }

    const option = {
      legend: {
        data: seriesData.map((item, index) => ({
          type: util.const.SERIES_2_LEGEND_TYPE_MAP.pie,
          name: item.name as unknown as string,
          selected: true
        }))
      },
      tooltip: {
        formatter: params => {
          const series = Array.isArray(params) ? params : [params];
          const items = [] as Parameters<typeof hook.buildTooltip>[0]['items'];

          series.forEach(item => {
            const result = hook.formatDataNumberValue(item.value, {
              type: 'table',
              unit: dataMetaInfo[item.seriesName]?.unit
            });

            items.push({
              type: util.const.SERIES_2_LEGEND_TYPE_MAP[item.seriesType],
              color: item.color,
              name: item.name,
              value: result.value + result.unit
            });
          });

          return hook.buildTooltip({
            config: hook.buildStandardChartPresetOption(api).tooltip,
            title: series?.[0]?.seriesName,
            items
          });
        }
      },
      xAxis: [],
      yAxis: [],
      series: [
        {
          ...(seriesOption as Record<string, unknown>),
          type: 'pie',
          name: y,
          AIGCEncoding: spec.encoding,
          data: seriesData
        }
      ]
    };

    return proxy
      ? {
          xAxis: option.xAxis,
          yAxis: option.yAxis,
          series: option.series.map(item => {
            return {
              ...(seriesOption as Record<string, unknown>),
              type: item.type,
              AIGCEncoding: item.AIGCEncoding,
              data: item.data
            };
          })
        }
      : option;
  }
});
